package com.havn.app.features.clients.presentation

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.havn.app.core.designsystem.EmptyState
import com.havn.app.core.designsystem.HavnBlack
import com.havn.app.core.designsystem.HavnButton
import com.havn.app.core.designsystem.HavnButtonStyle
import com.havn.app.core.designsystem.HavnCard
import com.havn.app.core.designsystem.HavnCardBorder
import com.havn.app.core.designsystem.HavnDanger
import com.havn.app.core.designsystem.HavnGold
import com.havn.app.core.designsystem.HavnIcon
import com.havn.app.core.designsystem.HavnIconKey
import com.havn.app.core.designsystem.HavnInfiniteListLoadEffect
import com.havn.app.core.designsystem.HavnPullToRefreshBox
import com.havn.app.core.designsystem.HavnIvory
import com.havn.app.core.designsystem.HavnListDefaults
import com.havn.app.core.designsystem.HavnListRowSeparator
import com.havn.app.core.designsystem.HavnPushNavigation
import com.havn.app.core.designsystem.HavnScreenFrame
import com.havn.app.core.designsystem.HavnSearchField
import com.havn.app.core.designsystem.HavnSkeletonBlock
import com.havn.app.core.designsystem.HavnSkeletonListFooter
import com.havn.app.core.designsystem.HavnSpacing
import com.havn.app.core.designsystem.HavnStatus
import com.havn.app.core.designsystem.HavnStatusBadge
import com.havn.app.core.designsystem.HavnSurfaceElevated
import com.havn.app.core.designsystem.HavnTextField
import com.havn.app.core.designsystem.HavnTextMuted
import com.havn.app.core.designsystem.HavnTextPrimary
import com.havn.app.core.designsystem.HavnTextSecondary
import com.havn.app.core.designsystem.HavnTopHeaderTextStyle
import com.havn.app.core.designsystem.HavnWebRefreshAction
import com.havn.app.core.designsystem.LoadingState
import com.havn.app.core.designsystem.havnIsWebShell
import com.havn.app.core.designsystem.havnNeedsNativeTabBarContentInset
import com.havn.app.core.designsystem.havnRefreshStartGate
import com.havn.app.core.localization.LocalHavnStrings
import com.havn.app.core.media.HavnAsyncImage
import com.havn.app.core.media.HavnDatabaseImage
import com.havn.app.core.media.HavnPickedPhoto
import com.havn.app.core.media.HavnPhotoPicker
import com.havn.app.core.media.havnContactAvatarPreviewSource
import com.havn.app.core.media.havnPropertyPhotoPreviewSource
import com.havn.app.core.platform.HavnExternalActions
import com.havn.app.core.platform.havnIsTabletDevice
import com.havn.app.core.qr.HavnQrCode
import com.havn.app.features.clients.domain.Client
import com.havn.app.features.clients.domain.ClientInquiry
import com.havn.app.features.clients.domain.ClientOpening
import com.havn.app.features.openhouses.domain.LeadTemperature
import com.havn.app.features.leads.presentation.LeadTemperatureSegmentedControl
import com.havn.app.features.tasks.domain.HavnTask
import com.havn.app.features.tasks.presentation.LinkedTasksSection
import com.havn.app.ui.theme.LocalHavnThemeTokens

@Composable
fun ClientsScreen(
    viewModel: ClientsViewModel,
    photoPicker: HavnPhotoPicker,
    onCreateTask: (Client) -> Unit = {},
    onOpenOpening: (String) -> Unit = {},
    onOpenLead: (String) -> Unit = {},
    detailBackLabel: String? = null,
    onBackFromDetail: (() -> Unit)? = null,
    linkedTasks: List<HavnTask> = emptyList(),
    onOpenTask: (String) -> Unit = {},
    onUpgradePlan: () -> Unit = {},
    scrollToTopSignal: Int = 0
) {
    val state by viewModel.uiState.collectAsState()
    val strings = LocalHavnStrings.current
    val listState = rememberLazyListState()
    HavnPushNavigation(
        targetState = state.mode,
        depthFor = ClientsMode::navigationDepth
    ) { mode ->
        when (mode) {
            ClientsMode.List -> ClientsListHome(state, viewModel, listState, scrollToTopSignal, onUpgradePlan)
            ClientsMode.Add -> ClientFormScreen(state, viewModel, photoPicker, isEdit = false, onUpgradePlan = onUpgradePlan)
            ClientsMode.Edit -> ClientFormScreen(state, viewModel, photoPicker, isEdit = true, onUpgradePlan = onUpgradePlan)
            ClientsMode.Detail -> ClientDetailScreen(state, viewModel, photoPicker, onCreateTask, onOpenOpening, onOpenLead, detailBackLabel, onBackFromDetail, linkedTasks, onOpenTask, onUpgradePlan)
            ClientsMode.AddInquiry -> GlobalInquiryScreen(state, viewModel)
            ClientsMode.EditInquiry -> GlobalInquiryScreen(state, viewModel, isEdit = true)
        }
    }

    if (state.showDeleteConfirmation) {
        AlertDialog(
            onDismissRequest = viewModel::dismissDeleteConfirmation,
            title = { Text(strings.deleteClientTitle) },
            text = { Text(strings.deleteClientBody) },
            confirmButton = {
                TextButton(onClick = viewModel::confirmDeleteSelected) {
                    Text(strings.commonDelete, color = HavnDanger)
                }
            },
            dismissButton = {
                TextButton(onClick = viewModel::dismissDeleteConfirmation) {
                    Text(strings.commonCancel)
                }
            }
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun ClientsListHome(
    state: ClientsUiState,
    viewModel: ClientsViewModel,
    listState: LazyListState,
    scrollToTopSignal: Int,
    onUpgradePlan: () -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    var handledScrollToTopSignal by remember { mutableStateOf(scrollToTopSignal) }
    LaunchedEffect(scrollToTopSignal) {
        if (scrollToTopSignal > handledScrollToTopSignal) {
            if (listState.firstVisibleItemIndex == 0 && listState.firstVisibleItemScrollOffset == 0) {
                viewModel.refresh()
            } else {
                listState.animateScrollToItem(0)
            }
        }
        handledScrollToTopSignal = scrollToTopSignal
    }
    var gestureStartedAtTop by remember { mutableStateOf(false) }
    val bottomContentPadding = if (havnNeedsNativeTabBarContentInset) 132.dp else 18.dp
    HavnInfiniteListLoadEffect(
        listState = listState,
        enabled = state.hasMoreClients,
        isLoading = state.isLoading || state.isLoadingMore,
        itemCount = state.clients.size,
        onLoadMore = viewModel::loadMoreClients
    )

    HavnPullToRefreshBox(
        isRefreshing = state.isLoading,
        onRefresh = {
            if (gestureStartedAtTop) {
                viewModel.refresh()
            }
        },
        modifier = Modifier
            .fillMaxSize()
            .havnRefreshStartGate(listState) { gestureStartedAtTop = it }
            .background(Brush.verticalGradient(listOf(theme.gradientStart, theme.background, theme.gradientEnd)))
    ) {
        LazyColumn(
            state = listState,
            modifier = Modifier
                .fillMaxSize(),
            contentPadding = PaddingValues(start = 18.dp, top = 18.dp, end = 18.dp, bottom = bottomContentPadding),
            verticalArrangement = Arrangement.spacedBy(HavnListDefaults.headerToSearchGap)
        ) {
            item("clients-header") {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = strings.clientsTitle,
                        modifier = Modifier.weight(1f),
                        color = theme.primaryText,
                        style = HavnTopHeaderTextStyle
                    )
                    IconCircleButton(
                        icon = HavnIconKey.Plus,
                        contentDescription = strings.addClient,
                        onClick = viewModel::startAdd
                    )
                }
            }

            state.message?.let { message ->
                item("clients-message") { InlineMessage(message, theme.primaryAccent, onDismiss = viewModel::clearMessage) }
            }
            state.error?.let { error ->
                item("clients-error") {
                    ErrorPanel(error = error, onDismiss = viewModel::clearError, onUpgradePlan = onUpgradePlan)
                }
            }

            item("clients-search") {
                HavnSearchField(
                    value = state.searchQuery,
                    onValueChange = viewModel::updateSearchQuery,
                    placeholder = strings.searchClients
                )
            }

            when {
                (!state.hasLoadedInitialPage || state.isLoading) && state.clients.isEmpty() -> {
                    items(8, key = { "client-skeleton-$it" }) {
                        ClientCardSkeleton()
                    }
                }
                state.clients.isEmpty() -> {
                    item("clients-empty") {
                        EmptyState(
                            title = strings.noClientsYetTitle,
                            body = strings.noClientsYetBody
                        )
                    }
                }
                state.clients.isEmpty() && state.searchQuery.isNotBlank() -> {
                    item("clients-search-empty") {
                        EmptyState(
                            title = strings.noClientsFoundTitle,
                            body = strings.noClientsFoundBody
                        )
                    }
                }
                else -> {
                    items(state.clients, key = { it.id }) { client ->
                        ClientListCard(
                            client = client,
                            onClick = { viewModel.openClient(client.id) }
                        )
                        HavnListRowSeparator()
                    }
                    if (state.hasMoreClients) {
                        item("clients-loading-more") {
                            HavnSkeletonListFooter()
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ClientCardSkeleton() {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier.fillMaxWidth(),
        color = theme.card,
        shape = MaterialTheme.shapes.large,
        border = BorderStroke(1.dp, theme.border.copy(alpha = 0.55f))
    ) {
        Row(
            modifier = Modifier.padding(14.dp),
            horizontalArrangement = Arrangement.spacedBy(12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SkeletonBlock(Modifier.size(44.dp).clip(RoundedCornerShape(22.dp)))
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                SkeletonBlock(Modifier.fillMaxWidth(0.46f).height(15.dp))
                SkeletonBlock(Modifier.fillMaxWidth(0.68f).height(12.dp))
            }
            SkeletonBlock(Modifier.width(48.dp).height(20.dp))
        }
    }
}

@Composable
private fun SkeletonBlock(modifier: Modifier = Modifier) {
    HavnSkeletonBlock(modifier, shape = MaterialTheme.shapes.small)
}

@Composable
private fun ClientFormScreen(
    state: ClientsUiState,
    viewModel: ClientsViewModel,
    photoPicker: HavnPhotoPicker,
    isEdit: Boolean,
    onUpgradePlan: () -> Unit = {}
) {
    val strings = LocalHavnStrings.current
    HavnScreenFrame(
        eyebrow = "",
        title = if (isEdit) strings.editClient else strings.newClient,
        subtitle = if (isEdit) "" else strings.clientFormNewSubtitle,
        backLabel = if (isEdit) state.selectedClient?.fullName ?: strings.clientFallback else strings.clientsTitle,
        onBack = if (isEdit) viewModel::cancelForm else viewModel::openList
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
            ContactFormPhotoPicker(
                photo = state.form.pendingPhoto,
                existingPhotoUrl = if (isEdit) state.selectedClient?.avatarDataUrl else null,
                isSaving = state.isSaving,
                addLabel = strings.addPhoto,
                removeLabel = strings.deletePhoto,
                onPick = { photoPicker.pick(viewModel::updatePendingPhoto) },
                onRemove = {
                    if (state.form.pendingPhoto != null || !isEdit) {
                        viewModel.removePendingPhoto()
                    } else {
                        viewModel.deleteClientPhoto()
                    }
                }
            )
            HavnTextField(
                value = state.form.firstName,
                onValueChange = { value -> viewModel.updateForm { copy(firstName = value, validationError = null) } },
                label = strings.firstName,
                leadingIcon = HavnIconKey.User
            )
            HavnTextField(
                value = state.form.lastName,
                onValueChange = { value -> viewModel.updateForm { copy(lastName = value, validationError = null) } },
                label = strings.lastName,
                leadingIcon = HavnIconKey.User
            )
            HavnTextField(
                value = state.form.email,
                onValueChange = { value -> viewModel.updateForm { copy(email = value, validationError = null) } },
                label = strings.email,
                leadingIcon = HavnIconKey.Mail,
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email)
            )
            HavnTextField(
                value = state.form.phone,
                onValueChange = { value -> viewModel.updateForm { copy(phone = value, validationError = null) } },
                label = strings.phone,
                leadingIcon = HavnIconKey.Phone,
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone)
            )
            HavnTextField(
                value = state.form.notes,
                onValueChange = { value -> viewModel.updateForm { copy(notes = value) } },
                label = strings.comments,
                singleLine = false,
                leadingIcon = HavnIconKey.Note
            )
            state.form.validationError?.let { InlineMessage(it, Color(0xFFE85D4A)) }
            state.error?.let { ErrorPanel(error = it, onDismiss = viewModel::clearError, onUpgradePlan = onUpgradePlan) }
            HavnButton(
                text = if (isEdit) strings.saveClient else strings.createClient,
                onClick = { if (isEdit) viewModel.saveEditedClient(strings) else viewModel.saveClient(strings) },
                modifier = Modifier.fillMaxWidth(),
                loading = state.isSaving,
                leadingIcon = if (isEdit) HavnIconKey.Check else HavnIconKey.Plus
            )
        }
    }
}

@Composable
private fun ClientDetailScreen(
    state: ClientsUiState,
    viewModel: ClientsViewModel,
    photoPicker: HavnPhotoPicker,
    onCreateTask: (Client) -> Unit,
    onOpenOpening: (String) -> Unit,
    onOpenLead: (String) -> Unit,
    detailBackLabel: String?,
    onBackFromDetail: (() -> Unit)?,
    linkedTasks: List<HavnTask>,
    onOpenTask: (String) -> Unit,
    onUpgradePlan: () -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    val client = state.selectedClient
    HavnScreenFrame(
        eyebrow = "",
        title = client?.fullName ?: strings.clientFallback,
        subtitle = "",
        backLabel = detailBackLabel ?: strings.clientsTitle,
        onBack = onBackFromDetail ?: viewModel::openList,
        headerTrailing = client?.let { detailClient ->
            {
                HavnWebRefreshAction(
                    onRefresh = { viewModel.openClient(detailClient.id) },
                    isRefreshing = state.isDetailLoading
                )
            }
        }
    ) {
        if (client == null) {
            EmptyState(strings.clientUnavailableTitle, strings.clientUnavailableBody)
            return@HavnScreenFrame
        }
        val greetingName = client.firstName?.takeIf { it.isNotBlank() }
            ?: client.fullName.substringBefore(" ").ifBlank { strings.leadGenericGreetingName }

        Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
            HavnCard {
                BoxWithConstraints {
                    val wide = maxWidth >= 520.dp
                    val phoneLayout = !havnIsWebShell && !havnIsTabletDevice()
                    val showSideQr = wide && !phoneLayout
                    Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
                        Column(
                            modifier = Modifier.fillMaxWidth(),
                            verticalArrangement = Arrangement.spacedBy(14.dp)
                        ) {
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.spacedBy(16.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                ClientPhoto(client = client)
                                Column(
                                    modifier = Modifier.weight(1f),
                                    verticalArrangement = Arrangement.spacedBy(8.dp)
                                ) {
                                    Text(client.fullName, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
                                    Text(client.contactLine, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
                                    Text("${strings.clientPassLabel} ${client.walletPassKey.take(10).uppercase()}", color = theme.mutedText, style = MaterialTheme.typography.labelLarge)
                                }
                                if (showSideQr) {
                                    HavnQrCode(
                                        payload = client.qrPayload,
                                        modifier = Modifier.size(116.dp)
                                    )
                                }
                            }
                            if (phoneLayout) {
                                HavnQrCode(
                                    payload = client.qrPayload,
                                    modifier = Modifier.fillMaxWidth()
                                )
                            } else if (!showSideQr) {
                                Box(
                                    modifier = Modifier.fillMaxWidth(),
                                    contentAlignment = Alignment.Center
                                ) {
                                    HavnQrCode(
                                        payload = client.qrPayload,
                                        modifier = Modifier.size(156.dp)
                                    )
                                }
                            }
                            HavnButton(
                                text = strings.sendQr,
                                onClick = { viewModel.sendQrCode(client.id, strings) },
                                modifier = Modifier.fillMaxWidth(),
                                loading = state.isSaving,
                                leadingIcon = HavnIconKey.Send
                            )
                        }
                    }
                }
            }

            HavnCard {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(10.dp)
                ) {
                    ContactActionButton(
                        label = strings.leadActionCall,
                        icon = HavnIconKey.Phone,
                        onClick = { client.phone?.let(HavnExternalActions::openPhone) },
                        modifier = Modifier.weight(1f),
                        enabled = !client.phone.isNullOrBlank()
                    )
                    ContactActionButton(
                        label = strings.leadActionSms,
                        icon = HavnIconKey.Message,
                        onClick = {
                            client.phone?.let {
                                HavnExternalActions.openSms(
                                    to = it,
                                    body = "${strings.greetingHi} $greetingName,"
                                )
                            }
                        },
                        modifier = Modifier.weight(1f),
                        enabled = !client.phone.isNullOrBlank()
                    )
                    ContactActionButton(
                        label = strings.leadActionEmail,
                        icon = HavnIconKey.Mail,
                        onClick = {
                            client.email?.let {
                                HavnExternalActions.openEmail(
                                    to = it,
                                    subject = strings.followUpDefaultSubject,
                                    body = "${strings.greetingHi} $greetingName,"
                                )
                            }
                        },
                        modifier = Modifier.weight(1f),
                        enabled = !client.email.isNullOrBlank()
                    )
                }
            }

            HavnButton(
                text = strings.createTask,
                onClick = { onCreateTask(client) },
                modifier = Modifier.fillMaxWidth(),
                style = HavnButtonStyle.Secondary,
                leadingIcon = HavnIconKey.Check
            )
            LinkedTasksSection(
                tasks = linkedTasks.filter { it.contactId == client.id },
                onOpenTask = onOpenTask
            )

            state.message?.let { InlineMessage(it, theme.primaryAccent, onDismiss = viewModel::clearMessage) }
            state.error?.let { ErrorPanel(error = it, onDismiss = viewModel::clearError, onUpgradePlan = onUpgradePlan) }

            HavnCard {
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text(strings.comments, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
                    if (state.isEditingComments) {
                        HavnTextField(
                            value = state.commentsDraft,
                            onValueChange = viewModel::updateCommentsDraft,
                            label = strings.comments,
                            singleLine = false,
                            leadingIcon = HavnIconKey.Note
                        )
                    } else {
                        Text(
                            text = client.notes?.takeIf { it.isNotBlank() } ?: strings.noCommentsAdded,
                            color = theme.secondaryText,
                            style = MaterialTheme.typography.bodyMedium
                        )
                    }
                    HavnButton(
                        text = if (state.isEditingComments) strings.commonSave else strings.commonAdd,
                        onClick = { if (state.isEditingComments) viewModel.saveComments() else viewModel.startEditingComments() },
                        modifier = Modifier.fillMaxWidth(),
                        style = HavnButtonStyle.Secondary,
                        loading = state.isSaving,
                        leadingIcon = HavnIconKey.Note
                    )
                }
            }

            HavnCard {
                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Text(strings.generalInquiries, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
                    HavnButton(
                        text = strings.addGeneralInquiry,
                        onClick = viewModel::startGlobalInquiry,
                        modifier = Modifier.fillMaxWidth(),
                        style = HavnButtonStyle.Secondary,
                        leadingIcon = HavnIconKey.Plus
                    )
                    if (state.isDetailLoading) {
                        repeat(2) {
                            ClientOpeningSkeletonRow()
                        }
                    } else if (client.inquiries.none { it.propertyId == null }) {
                        Text(
                            strings.noGeneralInquiries,
                            color = theme.secondaryText,
                            style = MaterialTheme.typography.bodyMedium
                        )
                    } else {
                        client.inquiries.filter { it.propertyId == null }.forEach { inquiry ->
                            ClientInquiryRow(inquiry = inquiry, onClick = { viewModel.openGeneralInquiry(inquiry.leadId) })
                        }
                    }
                }
            }

            HavnCard {
                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Text(strings.propertyInquiries, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
                    if (state.isDetailLoading) {
                        repeat(2) { ClientOpeningSkeletonRow() }
                    } else if (client.inquiries.none { it.propertyId != null }) {
                        Text(strings.noPropertyInquiries, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
                    } else {
                        client.inquiries.filter { it.propertyId != null }.forEach { inquiry ->
                            ClientInquiryRow(inquiry = inquiry, onClick = { onOpenLead(inquiry.leadId) })
                        }
                    }
                }
            }

            HavnCard {
                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Text(strings.openingsTitle, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
                    if (state.isDetailLoading) {
                        repeat(3) {
                            ClientOpeningSkeletonRow()
                        }
                    } else if (client.openings.isEmpty()) {
                        Text(
                            strings.clientOpeningsEmpty,
                            color = theme.secondaryText,
                            style = MaterialTheme.typography.bodyMedium
                        )
                    } else {
                        client.openings.forEach { opening ->
                            ClientOpeningRow(
                                opening = opening,
                                onClick = { onOpenOpening(opening.openingId) }
                            )
                        }
                    }
                }
            }

            HavnButton(
                text = strings.commonEdit,
                onClick = viewModel::startEdit,
                modifier = Modifier.fillMaxWidth(),
                style = HavnButtonStyle.Secondary
            )
            HavnButton(
                text = strings.commonDelete,
                onClick = viewModel::requestDeleteSelected,
                modifier = Modifier.fillMaxWidth(),
                style = HavnButtonStyle.Danger,
                loading = state.isSaving
            )
        }
    }
}

@Composable
private fun GlobalInquiryScreen(state: ClientsUiState, viewModel: ClientsViewModel, isEdit: Boolean = false) {
    val strings = LocalHavnStrings.current
    val form = state.inquiryForm
    val contactName = state.selectedClient?.fullName.orEmpty()
    HavnScreenFrame(eyebrow = "", title = if (isEdit) strings.generalInquiry else strings.newGeneralInquiry, subtitle = contactName, backLabel = contactName, onBack = viewModel::cancelGlobalInquiry) {
        Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
            HavnCard {
                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Text(strings.inquiryStatus, style = MaterialTheme.typography.titleLarge)
                    LeadTemperatureSegmentedControl(
                        selectedTemperature = form.temperature,
                        includeAll = false,
                        enabled = !state.isSaving,
                        onTemperatureSelected = { temperature ->
                            temperature?.let { viewModel.updateInquiryForm(form.copy(temperature = it)) }
                        }
                    )
                }
            }
            HavnCard {
                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Text(strings.buyerLookingFor, style = MaterialTheme.typography.titleMedium)
                    HavnTextField(form.source, { viewModel.updateInquiryForm(form.copy(source = it)) }, strings.leadSource)
                    HavnTextField(form.location, { viewModel.updateInquiryForm(form.copy(location = it)) }, strings.locationCity)
                    Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                        HavnTextField(form.radiusKm, { viewModel.updateInquiryForm(form.copy(radiusKm = it)) }, strings.radiusKm, Modifier.weight(1f))
                        HavnTextField(form.propertyType, { viewModel.updateInquiryForm(form.copy(propertyType = it)) }, strings.propertyType, Modifier.weight(1f))
                    }
                    Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                        HavnTextField(form.budgetMin, { viewModel.updateInquiryForm(form.copy(budgetMin = it)) }, strings.budgetFrom, Modifier.weight(1f))
                        HavnTextField(form.budgetMax, { viewModel.updateInquiryForm(form.copy(budgetMax = it)) }, strings.budgetTo, Modifier.weight(1f))
                    }
                    HavnTextField(form.timeline, { viewModel.updateInquiryForm(form.copy(timeline = it)) }, strings.purchaseTimeline)
                    HavnTextField(form.notes, { viewModel.updateInquiryForm(form.copy(notes = it)) }, "Notes", singleLine = false)
                    state.error?.let { Text(it, color = HavnDanger) }
                    HavnButton(text = if (isEdit) strings.saveGeneralInquiry else strings.createGeneralInquiry, onClick = if (isEdit) viewModel::saveGeneralInquiryChanges else viewModel::saveGlobalInquiry, modifier = Modifier.fillMaxWidth(), loading = state.isSaving, leadingIcon = if (isEdit) HavnIconKey.Check else HavnIconKey.Plus)
                }
            }
        }
    }
}

@Composable
private fun ClientInquiryRow(inquiry: ClientInquiry, onClick: () -> Unit) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    Surface(
        onClick = onClick,
        modifier = Modifier.fillMaxWidth(),
        color = theme.surface,
        shape = MaterialTheme.shapes.medium,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Row(
            modifier = Modifier.padding(12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            HavnIcon(
                icon = if (inquiry.propertyId == null) HavnIconKey.Search else HavnIconKey.Home,
                contentDescription = null,
                tint = if (inquiry.propertyId == null) Color.Black else theme.primaryAccent,
                modifier = Modifier.size(20.dp)
            )
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(3.dp)) {
                Text(
                    text = inquiry.propertyTitle.ifBlank { strings.commonProperty },
                    color = theme.primaryText,
                    style = MaterialTheme.typography.labelLarge,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Text(
                    text = inquiry.propertyAddress.ifBlank { strings.commonAddress },
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodySmall,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                    Surface(
                        shape = RoundedCornerShape(6.dp),
                        color = if (inquiry.propertyId == null) Color.Black else Color.Transparent,
                        border = BorderStroke(1.dp, if (inquiry.propertyId == null) Color.Black else theme.border)
                    ) {
                        Text(
                            text = if (inquiry.propertyId == null) strings.generalInquiry else strings.propertyInquiry,
                            color = if (inquiry.propertyId == null) Color.White else theme.secondaryText,
                            style = MaterialTheme.typography.labelSmall,
                            fontWeight = FontWeight.SemiBold,
                            modifier = Modifier.padding(horizontal = 7.dp, vertical = 3.dp)
                        )
                    }
                    inquiry.createdAt?.substringBefore("T")?.takeIf { it.isNotBlank() }?.let {
                        Text(it, color = theme.mutedText, style = MaterialTheme.typography.labelSmall)
                    }
                }
            }
            HavnStatusBadge(status = inquiry.temperature.toTemperatureStatus())
        }
    }
}

@Composable
private fun ClientOpeningSkeletonRow() {
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = 6.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        HavnSkeletonBlock(Modifier.size(42.dp), shape = MaterialTheme.shapes.medium)
        Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(7.dp)) {
            HavnSkeletonBlock(Modifier.fillMaxWidth(0.56f).height(14.dp))
            HavnSkeletonBlock(Modifier.fillMaxWidth(0.74f).height(11.dp))
        }
        HavnSkeletonBlock(Modifier.width(70.dp).height(24.dp), shape = RoundedCornerShape(12.dp))
    }
}

@Composable
private fun ClientListCard(client: Client, onClick: () -> Unit) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    Surface(
        onClick = onClick,
        color = Color.Transparent,
        modifier = Modifier
            .fillMaxWidth()
            .height(64.dp)
    ) {
        Row(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 2.dp, vertical = 7.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            ContactListAvatar(client)
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.Center
            ) {
                Text(
                    text = client.fullName,
                    color = theme.primaryText,
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.SemiBold,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Text(
                    text = client.email?.takeIf { it.isNotBlank() }
                        ?: client.phone?.takeIf { it.isNotBlank() }
                        ?: strings.clientNoEmail,
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodySmall,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
            ContactOpeningMetric(
                value = client.openings.size.toString(),
                label = strings.openingsTitle
            )
        }
    }
}

@Composable
private fun ContactOpeningMetric(
    value: String,
    label: String
) {
    val theme = LocalHavnThemeTokens.current
    Column(
        horizontalAlignment = Alignment.End,
        verticalArrangement = Arrangement.spacedBy(3.dp)
    ) {
        Row(
            horizontalArrangement = Arrangement.spacedBy(5.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            HavnIcon(
                icon = HavnIconKey.Calendar,
                contentDescription = null,
                tint = theme.secondaryText,
                modifier = Modifier.size(14.dp)
            )
            Text(
                value,
                color = theme.primaryText,
                style = MaterialTheme.typography.titleSmall,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
        Text(
            label,
            color = theme.mutedText,
            style = MaterialTheme.typography.labelSmall,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
    }
}

@Composable
private fun ContactListAvatar(client: Client) {
    val theme = LocalHavnThemeTokens.current
    Box(
        modifier = Modifier
            .size(42.dp)
            .clip(RoundedCornerShape(21.dp))
            .background(Brush.linearGradient(listOf(theme.secondaryAccent.copy(alpha = 0.72f), theme.surface)))
            .border(1.dp, theme.primaryAccent.copy(alpha = 0.45f), RoundedCornerShape(21.dp)),
        contentAlignment = Alignment.Center
    ) {
        val rendered = HavnAsyncImage(
            dataUrl = client.avatarThumbnailDataUrl,
            contentDescription = client.fullName,
            modifier = Modifier.fillMaxSize(),
            contentScale = ContentScale.Crop
        )
        if (!rendered) {
            Text(
                text = client.initials,
                color = theme.primaryText,
                style = MaterialTheme.typography.labelLarge,
                fontWeight = FontWeight.Bold
            )
        }
    }
}

@Composable
private fun ContactFormPhotoPicker(
    photo: HavnPickedPhoto?,
    existingPhotoUrl: String?,
    isSaving: Boolean,
    addLabel: String,
    removeLabel: String,
    onPick: () -> Unit,
    onRemove: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = MaterialTheme.shapes.large,
        color = theme.card,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Row(
            modifier = Modifier.padding(12.dp),
            horizontalArrangement = Arrangement.spacedBy(12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier
                    .size(116.dp)
                    .clip(MaterialTheme.shapes.medium)
                    .background(theme.surfaceElevated),
                contentAlignment = Alignment.Center
            ) {
                val rendered = HavnDatabaseImage(
                    dataUrl = photo?.thumbnailDataUrl ?: existingPhotoUrl,
                    previewDataUrl = if (photo == null) havnContactAvatarPreviewSource(existingPhotoUrl, 120) else null,
                    contentDescription = photo?.fileName ?: addLabel,
                    modifier = Modifier.fillMaxSize(),
                    contentScale = ContentScale.Crop
                )
                if (!rendered) {
                    HavnIcon(HavnIconKey.User, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(28.dp))
                }
            }
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                HavnButton(
                    text = addLabel,
                    onClick = onPick,
                    style = HavnButtonStyle.Secondary,
                    enabled = !isSaving,
                    modifier = Modifier.fillMaxWidth(),
                    leadingIcon = HavnIconKey.Plus
                )
                if (photo != null || !existingPhotoUrl.isNullOrBlank()) {
                    HavnButton(
                        text = removeLabel,
                        onClick = onRemove,
                        style = HavnButtonStyle.Secondary,
                        enabled = !isSaving,
                        modifier = Modifier.fillMaxWidth(),
                        leadingIcon = HavnIconKey.Trash
                    )
                }
            }
        }
    }
}

@Composable
private fun ClientPhoto(
    client: Client,
    onClick: (() -> Unit)? = null
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    val modifier = Modifier
        .size(116.dp)
        .then(if (onClick != null) Modifier.clickable(onClick = onClick) else Modifier)
    Surface(
        modifier = modifier,
        shape = MaterialTheme.shapes.large,
        color = theme.background,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Box(contentAlignment = Alignment.Center) {
            val rendered = HavnDatabaseImage(
                dataUrl = client.avatarDataUrl,
                previewDataUrl = havnContactAvatarPreviewSource(client.avatarDataUrl, 160),
                contentDescription = client.fullName,
                modifier = Modifier.fillMaxSize(),
                contentScale = androidx.compose.ui.layout.ContentScale.Crop
            )
            if (!rendered) {
                Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    HavnIcon(HavnIconKey.User, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(28.dp))
                    Text(strings.photo, color = theme.mutedText, style = MaterialTheme.typography.labelSmall)
                }
            }
        }
    }
}

@Composable
private fun ClientOpeningRow(
    opening: ClientOpening,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick),
        shape = MaterialTheme.shapes.medium,
        color = theme.surface,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Row(
            modifier = Modifier.padding(10.dp),
            horizontalArrangement = Arrangement.spacedBy(12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier
                    .size(74.dp)
                    .clip(MaterialTheme.shapes.medium)
                    .background(theme.card),
                contentAlignment = Alignment.Center
            ) {
                val rendered = HavnAsyncImage(
                    dataUrl = opening.propertyPhotoDataUrl,
                    contentDescription = opening.propertyTitle,
                    modifier = Modifier.fillMaxSize(),
                    contentScale = ContentScale.Crop
                )
                if (!rendered) {
                    HavnIcon(HavnIconKey.Home, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(22.dp))
                }
            }
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(5.dp)
            ) {
                Text(opening.displayTitle, color = theme.primaryText, style = MaterialTheme.typography.titleSmall, maxLines = 1, overflow = TextOverflow.Ellipsis)
                Text(opening.propertyAddress, color = theme.secondaryText, style = MaterialTheme.typography.bodySmall, maxLines = 1, overflow = TextOverflow.Ellipsis)
                Text(opening.startsAt.readableClientOpeningDate(), color = theme.mutedText, style = MaterialTheme.typography.labelMedium)
            }
            Column(horizontalAlignment = Alignment.End, verticalArrangement = Arrangement.spacedBy(7.dp)) {
                HavnStatusBadge(status = opening.temperature.toTemperatureStatus())
                HavnIcon(HavnIconKey.ArrowRight, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(18.dp))
            }
        }
    }
}

private fun String.toTemperatureStatus(): HavnStatus = when (lowercase()) {
    "hot" -> HavnStatus.Hot
    "cold" -> HavnStatus.Cold
    else -> HavnStatus.Warm
}

private fun String.readableClientOpeningDate(): String {
    val date = substringBefore("T").takeIf { it.isNotBlank() } ?: return this
    val parts = date.split("-")
    if (parts.size != 3) return date
    val month = parts[1].toIntOrNull()?.let { clientOpeningMonthNames.getOrNull(it - 1) } ?: return date
    val day = parts[2].toIntOrNull() ?: return date
    return "$month $day"
}

private val clientOpeningMonthNames = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")

@Composable
private fun IconCircleButton(
    icon: HavnIconKey,
    contentDescription: String,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier
            .size(42.dp)
            .clickable(onClick = onClick),
        shape = MaterialTheme.shapes.large,
        color = theme.surface,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Box(contentAlignment = Alignment.Center) {
            HavnIcon(icon, contentDescription = contentDescription, tint = theme.primaryAccent, modifier = Modifier.size(20.dp))
        }
    }
}

private val Client.initials: String
    get() = fullName
        .split(" ")
        .filter { it.isNotBlank() }
        .take(2)
        .joinToString("") { it.first().uppercase() }
        .ifBlank { "H" }

@Composable
private fun ContactActionButton(
    label: String,
    icon: HavnIconKey,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true
) {
    val theme = LocalHavnThemeTokens.current
    val contentColor = if (enabled) theme.primaryAccent else theme.mutedText
    Surface(
        onClick = onClick,
        enabled = enabled,
        modifier = modifier.height(64.dp),
        shape = androidx.compose.foundation.shape.RoundedCornerShape(16.dp),
        color = theme.surface.copy(alpha = if (enabled) 0.74f else 0.42f),
        border = BorderStroke(1.dp, if (enabled) theme.primaryAccent.copy(alpha = 0.45f) else theme.border.copy(alpha = 0.55f))
    ) {
        Row(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 8.dp),
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically
        ) {
            HavnIcon(icon, contentDescription = label, tint = contentColor, modifier = Modifier.size(19.dp))
            Spacer(Modifier.width(7.dp))
            Text(
                label,
                color = if (enabled) theme.primaryText else theme.mutedText,
                style = MaterialTheme.typography.labelLarge,
                fontWeight = FontWeight.SemiBold,
                maxLines = 1,
                overflow = TextOverflow.Clip
            )
        }
    }
}

@Composable
private fun InlineMessage(text: String, color: Color, onDismiss: (() -> Unit)? = null, onUpgradePlan: (() -> Unit)? = null) {
    val strings = LocalHavnStrings.current
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = MaterialTheme.shapes.medium,
        color = color.copy(alpha = 0.12f),
        border = BorderStroke(1.dp, color.copy(alpha = 0.42f))
    ) {
        Column(
            modifier = Modifier.padding(12.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Text(text = text, color = color, style = MaterialTheme.typography.bodyMedium)
            if (onUpgradePlan != null && text.isUpgradePrompt()) {
                HavnButton(
                    text = strings.upgradePlan,
                    onClick = onUpgradePlan,
                    modifier = Modifier.fillMaxWidth(),
                    style = HavnButtonStyle.Primary
                )
            }
            onDismiss?.let {
                HavnButton(
                    text = strings.commonDismiss,
                    onClick = it,
                    modifier = Modifier.fillMaxWidth(),
                    style = HavnButtonStyle.Ghost
                )
            }
        }
    }
}

@Composable
private fun ErrorPanel(error: String, onDismiss: () -> Unit, onUpgradePlan: () -> Unit = {}) {
    val strings = LocalHavnStrings.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
            Text(error, color = HavnDanger, style = MaterialTheme.typography.bodyLarge)
            if (error.isUpgradePrompt()) {
                HavnButton(
                    text = strings.upgradePlan,
                    onClick = onUpgradePlan,
                    style = HavnButtonStyle.Primary,
                    modifier = Modifier.fillMaxWidth()
                )
            }
            HavnButton(
                text = strings.commonDismiss,
                onClick = onDismiss,
                style = HavnButtonStyle.Ghost,
                modifier = Modifier.fillMaxWidth()
            )
        }
    }
}

private fun String.isUpgradePrompt(): Boolean {
    val normalized = lowercase()
    return "upgrade" in normalized || "current plan" in normalized || "limit reached" in normalized || "storage limit" in normalized
}

private fun ClientsMode.navigationDepth(): Int = when (this) {
    ClientsMode.List -> 0
    ClientsMode.Add,
    ClientsMode.Edit,
    ClientsMode.Detail -> 1
    ClientsMode.AddInquiry -> 2
    ClientsMode.EditInquiry -> 2
}
